import type { CSSProperties } from 'react';

interface Margins {
  top: string;
  right: string;
  bottom: string;
  left: string;
}

// CSS shorthand: which value index each side takes for 1..4 values
const SHORTHAND_INDEXES: Record<number, number[]> = {
  1: [0, 0, 0, 0],
  2: [0, 1, 0, 1],
  3: [0, 1, 2, 1],
  4: [0, 1, 2, 3],
};
const DEFAULT_WIDTHS = ['100%', 'auto'];

export function parseMargin(margin?: string | number): Margins | null {
  const text = typeof margin === 'number' ? `${margin}px` : margin ?? '';
  // calc()/var() can contain spaces; leave those values untouched
  if (text.includes('(')) return null;

  const values = text.trim().split(/\s+/).filter(Boolean);
  const indexes = SHORTHAND_INDEXES[values.length];
  if (!indexes) return null;

  const [top, right, bottom, left] = indexes.map((i) => values[i]);
  return { top, right, bottom, left };
}

const isSet = (value?: string) => (parseFloat(value ?? '') || 0) > 0;
const isNonZero = (value?: string) => (parseFloat(value ?? '') || 0) !== 0;

export function hasHorizontalMargin(margin?: string | number): boolean {
  const margins = parseMargin(margin);
  return !!margins && (isSet(margins.left) || isSet(margins.right));
}

// L/R margins on a full-width box shift it sideways; auto-size it so they narrow it.
export function withHorizontalMarginWidth<T extends CSSProperties>(
  styles: T
): T {
  if (!styles || !hasHorizontalMargin(styles.margin)) return styles;
  if (styles.width && !DEFAULT_WIDTHS.includes(styles.width as string))
    return styles;

  return { ...styles, width: 'auto', alignSelf: 'stretch' };
}

// Drops zero top/bottom margins so the spacing rules in pageBuilderParser.css still apply.
// Always auto-sizes: the CSS forces every text block to width: 100% anyway.
export function withHorizontalMarginOnly<T extends CSSProperties>(
  styles: T
): T {
  if (!styles || !hasHorizontalMargin(styles.margin)) return styles;

  const { margin, ...rest } = styles;
  const { top, right, bottom, left } = parseMargin(margin) as Margins;

  return {
    ...rest,
    width: 'auto',
    alignSelf: 'stretch',
    marginRight: right,
    marginLeft: left,
    ...(isNonZero(top) && { marginTop: top }),
    ...(isNonZero(bottom) && { marginBottom: bottom }),
  } as T;
}
